import { invalidateTracesList, traceKey } from "@common/api"
import {
  aircraftBaseKey,
  fittedComponentsBaseKey,
  fittedComponentsRootKey,
  modStatusBaseKey,
  modStatusRootKey,
} from "@common/api/aircraft"
import { approvedVendorsBaseKey } from "@common/api/approved-vendors"
import { atlPagesBaseKey } from "@common/api/atl"
import {
  adComplianceBaseKey,
  sbComplianceBaseKey,
} from "@common/api/compliance"
import {
  customerAddressesKey,
  customerContactBaseKey,
  customerContactsKey,
  customerKey,
  customersKey,
} from "@common/api/customers"
import { departmentsBaseKey } from "@common/api/departments"
import { documentsBaseKey } from "@common/api/documents"
import { emailsBaseKey } from "@common/api/emails"
import { facilitiesBaseKey } from "@common/api/facilities"
import { forecastsBaseKey } from "@common/api/forecast"
import { locationsBaseKey, locationsKey } from "@common/api/locations"
import { programmesBaseKey } from "@common/api/maintenance-programme"
import { notificationsKey } from "@common/api/notifications"
import { partBalancesBaseKey } from "@common/api/part-balances"
import { partPricesBaseKey } from "@common/api/part-prices"
import { partReservationsBaseKey } from "@common/api/part-reservations"
import { partStockBaseKey } from "@common/api/part-stock"
import { partsBaseKey } from "@common/api/parts"
import { positionsBaseKey } from "@common/api/positions"
import {
  cannedWorkPackageKey,
  cannedWorkPackagesBaseKey,
  cannedWorkTaskKey,
  cannedWorkTasksBaseKey,
} from "@common/api/quoting/canned"
import { dispositionsBaseKey } from "@common/api/quoting/disposition"
import { quoteRefetchKeys } from "@common/api/quoting/quote"
import {
  quoteNumberSequenceKey,
  tenantQuotingSettingsKey,
} from "@common/api/quoting/settings"
import {
  squawkCommentsKey,
  squawkPriceHistoryKey,
  squawksBaseKey,
} from "@common/api/quoting/squawk"
import { taskGroupsBaseKey } from "@common/api/quoting/task-group"
import { rateCardsBaseKey } from "@common/api/rate-cards"
import {
  allTaskTranscriptsBaseKey,
  latestTieInsReferencesKey,
} from "@common/api/task-transcripts"
import { taxRatesBaseKey } from "@common/api/tax-rates"
import { teamsBaseKey } from "@common/api/teams"
import {
  hubSpotEligibilityKey,
  hubSpotPropertyMappingKey,
  tenantIntegrationsBaseKey,
} from "@common/api/tenant-integrations"
import {
  timeEntriesBaseKey,
  timeEntryTasksBaseKey,
  timerKey,
} from "@common/api/time-entries"
import { toolCategoriesBaseKey } from "@common/api/tool-categories"
import { toolsBaseKey } from "@common/api/tools"
import { evaluationReportKey, sbEvaluationReportKey } from "@common/api/traces"
import {
  aircraftUtilizationKey,
  partInstanceUtilizationKey,
  utilizationBaseKey,
} from "@common/api/utilization"
import {
  changedWorkPackageKeys,
  workPackageBaseKey,
  workPackageNumberSequenceKey,
  workPackageSettingsKey,
  workPackagesKey,
} from "@common/api/work-packages"
import { workTaskPartsBaseKey } from "@common/api/work-task-parts"
import type { TracePublic } from "@common/client"
import { changedSince, type UIState } from "@common/lib/ui-state"
import { usePostHog } from "@posthog/react"
import {
  type QueryClient,
  type QueryKey,
  useQueryClient,
} from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react"

export type MessageAttachment = {
  document_id: string
  file_name: string
}

export type InputMetadata = {
  attachment_ids: string[]
}

export type OutputMetadata = {
  attachments: MessageAttachment[]
}

export type TextMessage = {
  type: "text"
  role: "agent" | "user"
  text: string
  historical?: boolean
  metadata?: OutputMetadata
}

export type DeltaMessage = {
  type: "delta"
  delta: string
  historical?: boolean
}

export type ProgressMessage = {
  type: "progress"
  message: string
}

export type SubagentProgressMessage = {
  type: "subagent_progress"
  id: string
  name: string
  message: string
}

export type Subagent = {
  name: string
  message: string
}

export type SubagentDoneMessage = {
  type: "subagent_done"
  id: string
}

export type ToolStartMessage = {
  type: "tool_start"
  name: string
  historical?: boolean
}

export type ToolDeltaMessage = {
  type: "tool_delta"
  output: string
  name: string
  historical?: boolean
}

export type ToolMessage = {
  type: "tool"
  name: string
  output: unknown
  stopped: boolean
  historical?: boolean
}

export type ChatStatus = "connecting" | "connected" | "disconnected"

export type AgentSource = "extension" | "web"

export type ChatFeature =
  | "sandbox"
  | "thinking"
  | "progress"
  | "title"
  | "refetch"
  | "squawk_tool"

export type AgentTurn = {
  role: "agent"
  text?: TextMessage
  tools: Record<string, ToolMessage | ToolStartMessage>
  streaming: boolean
  historical: boolean
  stopped?: StoppedAt
}

export type UserTurn = {
  role: "user"
  text: TextMessage
}

export type Turn = AgentTurn | UserTurn

export type ChatMessage =
  | TextMessage
  | ToolMessage
  | ToolStartMessage
  | StoppedMessage

export type DoneMessage = {
  type: "done"
  reason?: "interrupt" | "stopped"
}

export type StoppedAt = "thinking" | "answer"

export type StoppedMessage = {
  type: "stopped"
  at: StoppedAt
}

export type TitleMessage = {
  type: "title"
  title: string
}

export type RefetchMessage = {
  type: "refetch"
  resource: string
  id?: string | number | null
}

type ServerEvent =
  | TextMessage
  | DeltaMessage
  | ProgressMessage
  | SubagentProgressMessage
  | SubagentDoneMessage
  | ToolStartMessage
  | ToolDeltaMessage
  | ToolMessage
  | DoneMessage

type MessageAction = ServerEvent | { type: "reset" }

type ChatState = {
  turns: Turn[]
  thinking: boolean
  progress: string | null
  subagents: Record<string, Subagent>
}

function agentTurn(historical: boolean): AgentTurn {
  return { role: "agent", tools: {}, streaming: true, historical }
}

function isMergeable(obj: unknown): obj is { content: string } {
  return (
    !!obj &&
    typeof obj === "object" &&
    "content" in obj &&
    typeof obj.content === "string"
  )
}

function addTool(
  turn: AgentTurn,
  tool: ToolStartMessage | ToolMessage,
): AgentTurn {
  return { ...turn, tools: { ...turn.tools, [tool.name]: tool } }
}

function addToolDelta(turn: AgentTurn, delta: ToolDeltaMessage): AgentTurn {
  const existing = turn.tools[delta.name]

  if (!existing || existing.type === "tool_start") {
    return addTool(turn, {
      type: "tool",
      name: delta.name,
      output: delta.output,
      stopped: false,
    })
  }

  if (isMergeable(existing.output) && isMergeable(delta.output)) {
    const merged = existing.output.content + delta.output.content
    return addTool(turn, { ...existing, output: { content: merged } })
  }

  return turn
}

function stopTurn(turn: AgentTurn): AgentTurn {
  return {
    ...turn,
    streaming: false,
    stopped: turn.text ? "answer" : "thinking",
  }
}

function addText(turn: AgentTurn, text: TextMessage): AgentTurn {
  return { ...turn, text, streaming: false }
}

function addTextDelta(turn: AgentTurn, delta: DeltaMessage): AgentTurn {
  const prev = turn.text?.text ?? ""
  return {
    ...turn,
    text: { type: "text", role: "agent", text: prev + delta.delta },
  }
}

function refetchKeys({ resource, id }: RefetchMessage): readonly QueryKey[] {
  switch (resource) {
    case "quote":
      return quoteRefetchKeys(id == null ? undefined : String(id))
    case "aircraft":
      return id == null
        ? [
            aircraftBaseKey,
            utilizationBaseKey,
            fittedComponentsRootKey,
            modStatusRootKey,
          ]
        : [
            aircraftBaseKey,
            aircraftUtilizationKey(String(id)),
            fittedComponentsBaseKey(String(id)),
            modStatusBaseKey(String(id)),
          ]
    case "utilization":
      return id == null
        ? [utilizationBaseKey]
        : [partInstanceUtilizationKey(String(id))]
    case "atl_page":
      return [atlPagesBaseKey]
    case "forecast":
      return [forecastsBaseKey]
    case "time_entry":
      return [timerKey, timeEntriesBaseKey, timeEntryTasksBaseKey]
    case "programme_revision":
      return [programmesBaseKey]
    case "customer":
      return id == null
        ? [customersKey]
        : [customersKey, customerKey(String(id))]
    case "customer_contacts":
      return id == null
        ? [customerContactBaseKey]
        : [customerContactsKey(String(id)), customerContactBaseKey]
    case "customer_addresses":
      return id == null ? [] : [customerAddressesKey(String(id))]
    case "tools":
      return [toolsBaseKey]
    case "approved_vendor":
      return [approvedVendorsBaseKey]
    case "ad_assessment":
      return id == null
        ? [adComplianceBaseKey]
        : [evaluationReportKey(String(id)), adComplianceBaseKey]
    case "sb_assessment":
      return id == null
        ? [sbComplianceBaseKey]
        : [sbEvaluationReportKey(String(id)), sbComplianceBaseKey]
    case "locations":
      return id == null ? [locationsBaseKey] : [locationsKey(String(id))]
    case "tool_categories":
      return [toolCategoriesBaseKey, toolsBaseKey]
    case "work_order_settings":
      return [workPackageSettingsKey, workPackageNumberSequenceKey]
    case "crm_settings":
      return id == null
        ? [tenantIntegrationsBaseKey]
        : [
            tenantIntegrationsBaseKey,
            hubSpotEligibilityKey(String(id)),
            hubSpotPropertyMappingKey(String(id)),
          ]
    case "quote_dispositions":
      return [dispositionsBaseKey]
    case "quote_task_groups":
      return [taskGroupsBaseKey]
    case "quoting_settings":
      return [tenantQuotingSettingsKey, quoteNumberSequenceKey]
    case "departments":
      return [departmentsBaseKey]
    case "facilities":
      return [facilitiesBaseKey, locationsBaseKey]
    case "teams":
      return [teamsBaseKey]
    case "positions":
      return [positionsBaseKey]
    case "rate_cards":
      return [rateCardsBaseKey]
    case "tax_rates":
      return [taxRatesBaseKey]
    case "canned_work_packages":
      return id == null
        ? [cannedWorkPackagesBaseKey]
        : [cannedWorkPackagesBaseKey, cannedWorkPackageKey(String(id))]
    case "canned_work_tasks":
      return id == null
        ? [cannedWorkTasksBaseKey, cannedWorkPackagesBaseKey]
        : [
            cannedWorkTasksBaseKey,
            cannedWorkPackagesBaseKey,
            cannedWorkTaskKey(String(id)),
          ]
    case "squawk":
      return id == null
        ? [squawksBaseKey]
        : [
            squawksBaseKey,
            squawkCommentsKey(String(id)),
            squawkPriceHistoryKey(String(id)),
          ]
    case "email":
      return [emailsBaseKey]
    case "notification":
      return [notificationsKey]
    case "document":
      return [documentsBaseKey]
    case "task_transcript":
      return [allTaskTranscriptsBaseKey, latestTieInsReferencesKey]
    case "part":
      return [
        partsBaseKey,
        partStockBaseKey,
        partBalancesBaseKey,
        partPricesBaseKey,
        partReservationsBaseKey,
        workTaskPartsBaseKey,
      ]
    case "work_package":
      return id == null
        ? [workPackagesKey, workPackageBaseKey, squawksBaseKey]
        : changedWorkPackageKeys(String(id))
    default:
      return []
  }
}

function refetch(queryClient: QueryClient, message: RefetchMessage): void {
  for (const queryKey of refetchKeys(message)) {
    void queryClient.invalidateQueries({ queryKey })
  }
}

function chatReducer(state: ChatState, action: MessageAction): ChatState {
  const turns = state.turns
  const last = turns[turns.length - 1]
  const head = turns.slice(0, -1)
  const openAgent = last?.role === "agent" && last.streaming ? last : null

  switch (action.type) {
    case "reset":
      return {
        turns: [],
        thinking: false,
        progress: null,
        subagents: {},
      }

    case "done": {
      const done = { ...state, thinking: false, progress: null }

      if (action.reason !== "stopped") return done

      return {
        ...done,
        turns:
          last?.role === "agent"
            ? [...head, stopTurn(last)]
            : [...turns, stopTurn(agentTurn(false))],
      }
    }

    case "progress":
      return { ...state, progress: action.message, thinking: true }

    case "subagent_progress":
      return {
        ...state,
        subagents: {
          ...state.subagents,
          [action.id]: { name: action.name, message: action.message },
        },
      }

    case "subagent_done": {
      const { [action.id]: _, ...subagents } = state.subagents
      return { ...state, subagents }
    }

    case "tool":
    case "tool_start": {
      const started = action.type === "tool_start" && !action.historical
      return {
        ...state,
        thinking: state.thinking || started,
        turns: openAgent
          ? [...head, addTool(openAgent, action)]
          : [...turns, addTool(agentTurn(action.historical ?? false), action)],
      }
    }

    case "tool_delta":
      return {
        ...state,
        turns: openAgent
          ? [...head, addToolDelta(openAgent, action)]
          : [
              ...turns,
              addToolDelta(agentTurn(action.historical ?? false), action),
            ],
      }

    case "delta":
      return {
        ...state,
        turns: openAgent
          ? [...head, addTextDelta(openAgent, action)]
          : [
              ...turns,
              addTextDelta(agentTurn(action.historical ?? false), action),
            ],
      }

    case "text":
      if (action.role === "user") {
        return {
          ...state,
          turns: [...turns, { role: "user", text: action }],
          thinking: true,
          progress: null,
        }
      }
      return {
        ...state,
        turns: openAgent
          ? [...head, addText(openAgent, action)]
          : [...turns, addText(agentTurn(action.historical ?? false), action)],
      }

    default:
      return state
  }
}

const BASE_URL = import.meta.env.VITE_API_URL || ""
const WS_BASE_URL = BASE_URL.replace(/^http/, "ws")

export interface UseChat {
  sendMessage: (
    traceId: string,
    text: string,
    options: {
      source?: AgentSource
      features?: ChatFeature[]
      modeId?: string
      uiState?: UIState
      metadata?: InputMetadata
    },
  ) => Promise<void>
  ensureConnection: (traceId: string, features: ChatFeature[]) => Promise<void>
  stop: () => void
  connected: boolean
  messages: ChatMessage[]
  thinking: boolean
  progress: string | null
  subagents: Record<string, Subagent>
  reset: () => void
}

export function useChat(): UseChat {
  const posthog = usePostHog()
  const [connected, setConnected] = useState(false)
  const [state, dispatch] = useReducer(chatReducer, {
    turns: [],
    thinking: false,
    progress: null,
    subagents: {},
  })
  const queryClient = useQueryClient()
  const router = useRouter()
  const wsRef = useRef<WebSocket>(undefined)
  // Tracks an in-flight connection so concurrent callers reuse one socket.
  const socketPromiseRef = useRef<Promise<WebSocket> | null>(null)
  const uiStateRef = useRef<UIState>(undefined)

  const getSocket = useCallback(
    (traceId: string, features: ChatFeature[]) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        return Promise.resolve(wsRef.current)
      }

      // Reuse the pending connection so two near-simultaneous callers (e.g. React
      // StrictMode's double-invoked effects) don't open duplicate sockets — each
      // socket adds its own message listener, which would duplicate every message.
      if (socketPromiseRef.current) {
        return socketPromiseRef.current
      }

      const token = localStorage.getItem("access_token")

      if (!token) {
        return Promise.reject("Invalid token")
      }

      const params = new URLSearchParams(
        features.map((feature) => ["features", feature]),
      )
      const suffix = params.toString() ? `?${params}` : ""
      const ws = new WebSocket(`${WS_BASE_URL}/api/v1/trace-ws/${suffix}`)
      const timeout = setTimeout(() => ws.close(), 10_000)
      wsRef.current = ws

      const socketPromise = new Promise<WebSocket>((resolve, reject) => {
        let resolved: boolean = false

        ws.addEventListener("message", (event) => {
          const data = JSON.parse(event.data) as
            | ServerEvent
            | TitleMessage
            | RefetchMessage
            | { type: "auth" }
          if (data.type === "auth") {
            clearTimeout(timeout)
            resolved = true
            setConnected(true)
            resolve(ws)
            return
          }
          if (data.type === "title") {
            queryClient.setQueryData<TracePublic>(traceKey(traceId), (old) =>
              old ? { ...old, title: data.title } : old,
            )
            void invalidateTracesList(queryClient, { refetchType: "active" })
            void router.invalidate()
            return
          }
          if (data.type === "refetch") {
            refetch(queryClient, data)
            return
          }
          dispatch(data)
        })

        ws.addEventListener("open", () => {
          ws.send(JSON.stringify({ type: "auth", token, trace_id: traceId }))
        })

        ws.addEventListener("close", () => {
          if (wsRef.current === ws) {
            setConnected(false)
          }
          if (!resolved) {
            reject()
          }
        })
      })

      socketPromiseRef.current = socketPromise
      const clearPending = () => {
        if (socketPromiseRef.current === socketPromise) {
          socketPromiseRef.current = null
        }
      }
      socketPromise.then(clearPending, clearPending)

      return socketPromise
    },
    [queryClient, router],
  )

  const reset = useCallback(() => {
    dispatch({ type: "reset" })
    socketPromiseRef.current = null
    uiStateRef.current = undefined
    wsRef.current?.close()
  }, [])

  useEffect(() => {
    return () => reset()
  }, [reset])

  const sendMessage = useCallback(
    async (
      traceId: string,
      text: string,
      options: {
        source?: AgentSource
        features?: ChatFeature[]
        modeId?: string
        uiState?: UIState
        metadata?: InputMetadata
      },
    ) => {
      const socket = await getSocket(traceId, options.features ?? [])

      const next = options.uiState
      const uiState =
        next && uiStateRef.current
          ? changedSince(uiStateRef.current, next)
          : next

      if (next) {
        uiStateRef.current = next
      }

      const msg = JSON.stringify({
        type: "text",
        text,
        source: options.source,
        features: options.features,
        mode_id: options.modeId,
        metadata: options.metadata,
        ui_state: uiState,
        posthog_session_id: posthog?.get_session_id(),
      })
      socket.send(msg)
    },
    [getSocket, posthog],
  )

  const ensureConnection = useCallback(
    async (traceId: string, features: ChatFeature[]) => {
      await getSocket(traceId, features)
    },
    [getSocket],
  )

  const stop = useCallback(() => {
    const socket = wsRef.current
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: "stop" }))
    }
  }, [])

  const expanded: ChatMessage[] = useMemo(
    () =>
      state.turns.flatMap<ChatMessage>((message) =>
        message.role === "agent"
          ? [
              ...(message.text
                ? [{ ...message.text, historical: message.historical }]
                : []),
              ...Object.values(message.tools).map((tool) => ({
                ...tool,
                historical: message.historical,
              })),
              ...(message.stopped
                ? [{ type: "stopped" as const, at: message.stopped }]
                : []),
            ]
          : [message.text],
      ),
    [state.turns],
  )

  return {
    messages: expanded,
    thinking: state.thinking,
    progress: state.progress,
    subagents: state.subagents,
    sendMessage,
    ensureConnection,
    stop,
    connected,
    reset,
  }
}
